import { baseApi, setSessionMetadata, clearSessionMetadata, type ApiResponse } from './client';

/**
 * Admin Auth Types
 */
export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name?: string;
  phone_number?: string;
  avatar?: string;
  remember_me?: boolean;
  device?: DeviceInfo;
}

export interface LoginRequest {
  email: string;
  password: string;
  device?: DeviceInfo;
  remember_me?: boolean;
}

export interface LoginResponse {
  admin: AdminUser;
  authStrategy: string;
  device: DeviceInfo;
  session?: SessionInfo;
  device_id?: string;
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  fullName?: string;
  avatar?: string;
  roles?: string[];
  hasGoogle: boolean;
  hasApple: boolean;
  hasPassword: boolean;
  uuid: string;
  is_active?: boolean;
  isActive?: boolean;
  is_deleted: boolean;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  deleted_at?: string | null;
  google_id?: string | null;
  apple_id?: string | null;
  firebase_uid?: string | null;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  phone_number?: string | null;
  is_email_verified?: boolean;
  isEmailVerified?: boolean;
  is_suspended?: boolean;
  isSuspended?: boolean;
  email_verification_token?: string | null;
  email_verification_token_expires_at?: string | null;
  password_reset_token?: string | null;
  password_reset_token_expires_at?: string | null;
  last_login_at?: string | null;
  last_login_ip?: string | null;
  failed_login_attempts: number;
  locked_until?: string | null;
  login_method?: string;
  login_methods: string[];
  primary_login_method?: string | null;
  set_password_token?: string | null;
  set_password_token_expires_at?: string | null;
}

export interface DeviceInfo {
  device_id: string;
  device_name: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
  operating_system?: string;
  os_version?: string;
  browser?: string;
  browser_version?: string;
}

export interface SessionInfo {
  id: string;
  cookieName: string;
  expiresAt: string;
  maxAge: number;
}

export interface UserProfile {
  id: number;
  email: string;
  fullName: string;
  avatar?: string;
  roles: string[];
  isVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GoogleAuthRequest {
  token?: string;
  id_token?: string;
  redirectUrl?: string;
}

export interface AppleAuthRequest {
  code?: string;
  id_token?: string;
  redirectUrl?: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateProfileRequest {
  name?: string;
  avatar?: string;
  phone?: string;
  bio?: string;
}

export interface Device {
  id: string;
  name: string;
  type: string;
  browser?: string;
  os?: string;
  ip?: string;
  lastActive: string;
  isCurrent: boolean;
}

export interface Activity {
  id: string;
  action: string;
  description: string;
  ip?: string;
  device?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface Session {
  id: string;
  device: string;
  ip: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

/**
 * Admin Auth API
 * RTK Query endpoints for authentication and user management
 */
export const adminAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Register new admin user
     * Backend creates session via HTTP-only cookie
     */
    register: builder.mutation<ApiResponse<LoginResponse>, RegisterRequest>({
      query: (credentials) => ({
        url: '/admin/auth/register',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store user email for client-side reference
          if (data.result?.admin?.email) {
            setSessionMetadata(data.result.admin.email);
          }
        } catch {
          // Error handled by baseQuery
        }
      },
      invalidatesTags: ['Auth', 'User'],
    }),

    /**
     * Login with email and password
     * Backend creates session via HTTP-only cookie
     */
    login: builder.mutation<ApiResponse<LoginResponse>, LoginRequest>({
      query: (credentials) => ({
        url: '/admin/auth/login',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store user email for client-side reference
          if (data.result?.admin?.email) {
            setSessionMetadata(data.result.admin.email);
          }
        } catch {
          // Error handled by baseQuery
        }
      },
      invalidatesTags: ['Auth', 'User'],
    }),

    /**
     * Login with Google OAuth
     * Backend creates session via HTTP-only cookie
     */
    loginWithGoogle: builder.mutation<ApiResponse<LoginResponse>, GoogleAuthRequest>({
      query: (payload) => ({
        url: '/admin/auth/login/google',
        method: 'POST',
        body: payload,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store user email for client-side reference
          if (data.result?.admin?.email) {
            setSessionMetadata(data.result.admin.email);
          }
        } catch {
          // Error handled by baseQuery
        }
      },
      invalidatesTags: ['Auth', 'User'],
    }),

    /**
     * Login with Apple ID
     * Backend creates session via HTTP-only cookie
     */
    loginWithApple: builder.mutation<ApiResponse<LoginResponse>, AppleAuthRequest>({
      query: (payload) => ({
        url: '/admin/auth/login/apple',
        method: 'POST',
        body: payload,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Store user email for client-side reference
          if (data.result?.admin?.email) {
            setSessionMetadata(data.result.admin.email);
          }
        } catch {
          // Error handled by baseQuery
        }
      },
      invalidatesTags: ['Auth', 'User'],
    }),

    /**
     * Logout current user
     * Backend destroys session and clears HTTP-only cookie
     */
    logout: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/admin/auth/logout',
        method: 'POST',
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          await queryFulfilled;
          clearSessionMetadata();
        } catch {
          // Still clear metadata even if API call fails
          clearSessionMetadata();
        }
      },
      invalidatesTags: ['Auth', 'User', 'Profile'],
    }),

    /**
     * Verify email with token
     */
    verifyEmail: builder.mutation<ApiResponse<void>, VerifyEmailRequest>({
      query: (payload) => ({
        url: '/admin/auth/verify-email',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User', 'Profile'],
    }),

    /**
     * Resend verification email
     */
    resendVerificationEmail: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/admin/auth/resend-verification',
        method: 'POST',
      }),
    }),

    /**
     * Request password reset
     */
    forgotPassword: builder.mutation<ApiResponse<void>, ForgotPasswordRequest>({
      query: (payload) => ({
        url: '/admin/auth/forgot-password',
        method: 'POST',
        body: payload,
      }),
    }),

    /**
     * Reset password with token
     */
    resetPassword: builder.mutation<ApiResponse<void>, ResetPasswordRequest>({
      query: (payload) => ({
        url: '/admin/auth/reset-password',
        method: 'POST',
        body: payload,
      }),
    }),

    /**
     * Change password (authenticated)
     */
    changePassword: builder.mutation<ApiResponse<void>, ChangePasswordRequest>({
      query: (payload) => ({
        url: '/admin/auth/change-password',
        method: 'POST',
        body: payload,
      }),
    }),

    /**
     * Get current user profile
     */
    getProfile: builder.query<ApiResponse<UserProfile>, void>({
      query: () => '/admin/auth/profile',
      providesTags: ['Profile'],
    }),

    /**
     * Update user profile
     */
    updateProfile: builder.mutation<ApiResponse<UserProfile>, UpdateProfileRequest>({
      query: (payload) => ({
        url: '/admin/auth/profile',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Profile', 'User'],
    }),

    /**
     * Delete user account
     * Backend destroys session after account deletion
     */
    deleteAccount: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/admin/auth/account',
        method: 'DELETE',
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          await queryFulfilled;
          clearSessionMetadata();
        } catch {
          // Error handled by baseQuery
        }
      },
      invalidatesTags: ['Auth', 'User', 'Profile'],
    }),

    /**
     * Get all user devices
     */
    getDevices: builder.query<ApiResponse<Device[]>, void>({
      query: () => '/admin/auth/devices',
      providesTags: ['Device'],
    }),

    /**
     * Remove a device
     */
    removeDevice: builder.mutation<ApiResponse<void>, string>({
      query: (deviceId) => ({
        url: `/admin/auth/devices/${deviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Device'],
    }),

    /**
     * Get user activity log
     */
    getActivity: builder.query<ApiResponse<Activity[]>, { page?: number; limit?: number }>({
      query: (params) => ({
        url: '/admin/auth/activity',
        params,
      }),
      providesTags: ['Activity'],
    }),

    /**
     * Get active sessions
     */
    getSessions: builder.query<ApiResponse<Session[]>, void>({
      query: () => '/admin/auth/sessions',
      providesTags: ['Activity'],
    }),

    /**
     * Revoke a session
     */
    revokeSession: builder.mutation<ApiResponse<void>, string>({
      query: (sessionId) => ({
        url: `/admin/auth/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Activity'],
    }),

    /**
     * Revoke all sessions except current
     */
    revokeAllSessions: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/admin/auth/sessions/revoke-all',
        method: 'POST',
      }),
      invalidatesTags: ['Activity', 'Device'],
    }),
  }),
});

/**
 * Export hooks for components
 */
export const {
  useRegisterMutation,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useLoginWithAppleMutation,
  useLogoutMutation,
  useVerifyEmailMutation,
  useResendVerificationEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useDeleteAccountMutation,
  useGetDevicesQuery,
  useRemoveDeviceMutation,
  useGetActivityQuery,
  useGetSessionsQuery,
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
} = adminAuthApi;
